import type { Color, PieceSymbol } from 'chess.js';

interface PecaProps { tipo: PieceSymbol; cor: Color; className?: string }

const nomesPecas: Record<PieceSymbol, string> = {
  p: 'Peão', n: 'Cavalo', b: 'Bispo', r: 'Torre', q: 'Dama', k: 'Rei'
};

/**
 * Silhuetas no mesmo peso visual (viewBox 0 0 64 82).
 * Cavalo: contorno clássico tipo Wikipedia/cburnett (o SVG que estava bom).
 */
const formas: Record<PieceSymbol, React.ReactNode> = {
  p: (
    <>
      <path d="M41 22c0 7-4 12-9 15 2 8 9 11 11 18H21c2-7 9-10 11-18-5-3-9-8-9-15 0-10 18-10 18 0Z" />
      <path d="M20 57h24l4 7H16l4-7ZM14 67h36l4 8H10l4-8Z" />
    </>
  ),
  n: (
    <>
      <g transform="translate(4.2 4.5) scale(1.22)" strokeWidth="1.05">
        <path d="M22 10c10.5 1 16.5 8 16 29H15c0-9 10-6.5 8-21" />
        <path d="M24 18c.38 2.91-5.55 7.37-8 9-3 2-2.82 4.34-5 4-1.042-.94 1.41-3.04 0-3-1 0 .19 1.23-1 2-1 0-4.003 1-4-4 0-2 6-12 6-12s1.89-1.9 2-3.5c-.73-.994-.5-2-.5-3 1-1 3 2.5 3 2.5h2s.78-1.992 2.5-3c1 0 1 3 1 3" />
      </g>
      <path d="M14 57h36l4 7H10l4-7ZM10 66h44l3 8H7l3-8Z" />
    </>
  ),
  b: (
    <>
      <path d="M32 10c8 7 13 14 13 22 0 9-5 15-10 20h12l3 9H14l3-9h12c-5-5-10-11-10-20 0-8 5-15 13-22Z" />
      <path d="m36 19-9 15 8 8" fill="none" strokeLinecap="round" strokeWidth="1.5" opacity=".55" />
      <path d="M12 63h40l3 12H9l3-12Z" />
    </>
  ),
  r: (
    <>
      <path d="M15 11h9v8h7v-8h8v8h7v-8h9v17l-7 6v23H22V34l-7-6V11Z" />
      <path d="M20 28h30M20 56h30" fill="none" strokeLinecap="round" strokeWidth="1.4" opacity=".45" />
      <path d="M15 60h40l3 15H12l3-15Z" />
    </>
  ),
  q: (
    <>
      <circle cx="13" cy="16" r="4" />
      <circle cx="25" cy="9" r="4" />
      <circle cx="39" cy="9" r="4" />
      <circle cx="51" cy="16" r="4" />
      <path d="m14 21 8 31h20l8-31-11 12-7-18-7 18-11-12Z" />
      <path d="M18 55h28l4 8H14l4-8ZM12 66h40l4 9H8l4-9Z" />
    </>
  ),
  k: (
    <>
      <path d="M32 4v15M25 11h14" fill="none" strokeLinecap="round" strokeWidth="2.2" />
      <path d="M23 24c0-7 18-7 18 0 0 4-2 7-5 9 9 5 12 12 11 22H17c-1-10 2-17 11-22-3-2-5-5-5-9Z" />
      <path d="M16 57h32l4 8H12l4-8ZM10 68h44l3 7H7l3-7Z" />
    </>
  )
};

export function Peca({ tipo, cor, className = '' }: PecaProps) {
  const claro = cor === 'w';
  const id = `${tipo}-${cor}`;
  const forma = formas[tipo];
  return (
    <svg className={`peca-svg ${className}`} viewBox="0 0 64 82" role="img" aria-label={`${nomesPecas[tipo]} das ${cor === 'w' ? 'brancas' : 'pretas'}`}>
      <defs>
        <linearGradient id={`cor-${id}`} x1="0" y1="0" x2="1" y2="1">
          {claro ? (
            <>
              <stop stopColor="#fff9e9" />
              <stop offset=".48" stopColor="#e8dcc2" />
              <stop offset="1" stopColor="#a99a7d" />
            </>
          ) : (
            <>
              <stop stopColor="#6a635c" />
              <stop offset=".4" stopColor="#2e2a27" />
              <stop offset="1" stopColor="#12100e" />
            </>
          )}
        </linearGradient>
        <linearGradient id={`brilho-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop stopColor={claro ? '#ffffff' : '#c9bba8'} stopOpacity=".72" />
          <stop offset="1" stopColor="#000" stopOpacity=".12" />
        </linearGradient>
        <filter id={`sombra-${id}`} x="-25%" y="-25%" width="150%" height="160%">
          <feDropShadow dx="0" dy="3" stdDeviation="2.2" floodOpacity=".42" />
        </filter>
      </defs>
      {claro ? (
        <g
          fill={`url(#cor-${id})`}
          stroke="#776a55"
          strokeWidth="1.25"
          strokeLinejoin="round"
          filter={`url(#sombra-${id})`}
        >
          {forma}
        </g>
      ) : (
        <>
          {/* Halo claro: separa a silhueta preta das casas escuras */}
          <g
            fill="none"
            stroke="#efe2c8"
            strokeWidth="3.6"
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity=".94"
            filter={`url(#sombra-${id})`}
          >
            {forma}
          </g>
          <g
            fill={`url(#cor-${id})`}
            stroke="#1c1815"
            strokeWidth="1.2"
            strokeLinejoin="round"
            strokeLinecap="round"
          >
            {forma}
          </g>
        </>
      )}
      <path d="M18 61h28" stroke={`url(#brilho-${id})`} strokeWidth="1.2" opacity=".75" />
    </svg>
  );
}
